<template>
  <section class="monitor-section" aria-labelledby="service-heading">
    <header class="section-heading">
      <div>
        <h2 id="service-heading">{{ errorsOnly ? '服务异常' : '服务健康' }}</h2>
        <p>
          {{
            errorsOnly
              ? '展示当前不可用或部分异常的服务，点击名称查看错误详情。'
              : '只读连接检查与最近实际调用证据；外部调用证据超过 5 分钟后显示未验证。'
          }}
        </p>
      </div>
      <span
        >{{ store.filteredServices.length }} / {{ store.overview?.services.length ?? 0 }} 项</span
      >
    </header>
    <div class="filter-toolbar">
      <a-input
        v-model:value="store.serviceFilter.search"
        placeholder="搜索服务名称"
        allow-clear
        aria-label="搜索服务名称"
        ><template #prefix><i class="ri-search-line" aria-hidden="true" /></template
      ></a-input>
      <a-select
        v-model:value="store.serviceFilter.group"
        aria-label="服务分组"
        :options="groupOptions"
      />
      <a-radio-group
        v-if="!errorsOnly"
        v-model:value="store.serviceFilter.status"
        size="small"
        aria-label="服务状态筛选"
        ><a-radio-button value="all">全部</a-radio-button
        ><a-radio-button value="abnormal">只看异常</a-radio-button
        ><a-radio-button value="unknown">未验证</a-radio-button></a-radio-group
      >
    </div>
    <a-alert v-if="store.overviewError" type="error" show-icon :message="store.overviewError"
      ><template #action
        ><a-button size="small" @click="store.loadOverview">重试</a-button></template
      ></a-alert
    >
    <a-table
      :columns="columns"
      :data-source="store.filteredServices"
      row-key="id"
      size="middle"
      :pagination="false"
      :scroll="{ x: 'max-content' }"
      :loading="store.overviewLoading && !store.overview"
    >
      <template #emptyText
        ><a-empty
          :description="
            store.overviewError
              ? '服务诊断暂不可用'
              : errorsOnly && !store.serviceFilter.search && store.serviceFilter.group === 'all'
                ? '当前没有服务异常'
                : '没有符合筛选条件的服务'
          "
      /></template>
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'name'"
          ><button class="text-action service-name" @click="store.openDetail('service', record.id)">
            {{ record.name }}</button
          ><small>{{ groupLabels[record.group as MonitorGroup] }}</small></template
        >
        <a-tag
          v-else-if="column.key === 'status'"
          :color="statusColors[record.status as MonitorStatus]"
          >{{ statusLabels[record.status as MonitorStatus] }}</a-tag
        >
        <span v-else-if="column.key === 'metrics'" :title="facts(record)">{{
          facts(record, 3) || '—'
        }}</span>
        <template v-else-if="column.key === 'evidence'"
          ><span>{{ record.evidence.summary }}</span
          ><small v-if="record.evidence.errorCode" class="error-code">{{
            errorLabels[record.evidence.errorCode] ?? '未知错误分类'
          }}</small></template
        >
        <template v-else-if="column.key === 'time'"
          ><time>{{ formatTime(record.evidence.checkedAt) }}</time
          ><small
            >{{
              record.evidence.source === 'runtime'
                ? '实际运行'
                : record.evidence.source === 'configuration'
                  ? '配置检查'
                  : '只读探测'
            }}<template v-if="record.evidence.latencyMs !== null">
              · {{ record.evidence.latencyMs }} ms</template
            ></small
          ></template
        >
        <template v-else-if="column.key === 'action'"
          ><a-button type="link" size="small" @click="store.openDetail('service', record.id)"
            >诊断</a-button
          ><a-button
            v-if="record.id.startsWith('queue:')"
            type="link"
            size="small"
            @click="emit('open-queue', record.id.slice(6))"
            >查看 Job</a-button
          ></template
        >
      </template>
    </a-table>
  </section>
</template>

<script setup lang="ts">
import type { MonitorGroup, MonitorServiceView, MonitorStatus } from 'shared';
import { useAdminRuntimeMonitorStore } from '@/stores/admin-runtime-monitor.store';
import {
  errorLabels,
  formatTime,
  groupLabels,
  metricLabels,
  metricValue,
  statusColors,
  statusLabels,
} from './monitor-format';
const store = useAdminRuntimeMonitorStore();
defineProps<{ errorsOnly?: boolean }>();
const emit = defineEmits<{ 'open-queue': [id: string] }>();
const groupOptions = [
  { value: 'all', label: '全部分组' },
  ...Object.entries(groupLabels).map(([value, label]) => ({ value, label })),
];
const columns = [
  { title: '服务 / 分组', key: 'name' },
  { title: '状态', key: 'status', width: 110 },
  { title: '运行情况', key: 'metrics', width: 240, responsive: ['md'] },
  { title: '状态依据', key: 'evidence', width: 270, responsive: ['md'] },
  { title: '检查时间', key: 'time', width: 165, responsive: ['md'] },
  { title: '操作', key: 'action', width: 155, responsive: ['md'] },
];
function facts(row: MonitorServiceView, limit = 20): string {
  return Object.entries(row.metrics)
    .slice(0, limit)
    .map(([key, value]) => `${metricLabels[key] ?? key} ${metricValue(key, value)}`)
    .join(' · ');
}
</script>
